import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import {
  getUserPreferencesForUser,
  updateUserPreferences,
  subscribeToPreferences,
} from '../lib/account/userPreferences'
import type { UserPreferences, UserPreferencesUpdate } from '../types/userPreferences'

interface AccessibilityContextType {
  preferences: UserPreferences | null
  loading: boolean
  updatePreferences: (updates: UserPreferencesUpdate) => Promise<void>

  // Convenience getters
  language: string
  fontSize: string
  highContrast: boolean
  reduceMotion: boolean
  screenReaderOptimized: boolean
  dyslexiaFriendlyFont: boolean
  theme: string
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined)

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)

  // Load preferences when user signs in
  useEffect(() => {
    if (!user) {
      setPreferences(null)
      setLoading(false)
      return
    }

    let unsubscribe: (() => void) | undefined
    let cancelled = false

    async function loadPreferences() {
      try {
        const prefs = await getUserPreferencesForUser(user.id)

        // Don't update state if component unmounted
        if (cancelled) return

        setPreferences(prefs)

        unsubscribe = subscribeToPreferences(user.id, (updated) => {
          if (!cancelled) {
            setPreferences(updated)
          }
        })
      } catch (error) {
        if (cancelled) return

        // AbortError is expected during cleanup - don't log
        if (
          error instanceof Error &&
          (error.message.includes('AbortError') || error.message === 'No authenticated user')
        ) {
          setPreferences(null)
        } else {
          console.error('Error loading preferences:', error)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPreferences()

    return () => {
      cancelled = true
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [user])

  // Apply accessibility settings to document
  useEffect(() => {
    if (!preferences) return

    // Apply font size
    document.documentElement.style.setProperty(
      '--base-font-size',
      {
        small: '14px',
        medium: '16px',
        large: '18px',
        'x-large': '20px',
      }[preferences.font_size],
    )

    // Apply line spacing
    const lineSpacing = preferences.line_spacing || 'normal'
    document.documentElement.classList.remove(
      'line-spacing-normal',
      'line-spacing-comfortable',
      'line-spacing-wide',
    )
    document.documentElement.classList.add(`line-spacing-${lineSpacing}`)

    // Apply high contrast
    if (preferences.high_contrast) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }

    // Apply reduce motion
    if (preferences.reduce_motion) {
      document.documentElement.classList.add('reduce-motion')
    } else {
      document.documentElement.classList.remove('reduce-motion')
    }

    // Apply dyslexia-friendly font
    if (preferences.dyslexia_friendly_font) {
      document.documentElement.classList.add('dyslexia-font')
    } else {
      document.documentElement.classList.remove('dyslexia-font')
    }

    // Apply screen reader optimization
    if (preferences.screen_reader_optimized) {
      document.documentElement.classList.add('screen-reader-optimized')
    } else {
      document.documentElement.classList.remove('screen-reader-optimized')
    }

    // NOTE: Theme class management is handled by ThemeContext
    // Do NOT manipulate dark/light classes here to avoid conflicts
  }, [preferences])

  async function handleUpdatePreferences(updates: UserPreferencesUpdate) {
    console.log('[AccessibilityContext] handleUpdatePreferences called with:', updates)
    try {
      const updated = await updateUserPreferences(updates)
      console.log('[AccessibilityContext] Update successful:', updated)
      setPreferences(updated)
    } catch (error) {
      console.error('[AccessibilityContext] Error updating preferences:', error)
      throw error
    }
  }

  const value: AccessibilityContextType = {
    preferences,
    loading,
    updatePreferences: handleUpdatePreferences,

    // Convenience getters with fallbacks
    language: preferences?.language || 'en',
    fontSize: preferences?.font_size || 'medium',
    highContrast: preferences?.high_contrast || false,
    reduceMotion: preferences?.reduce_motion || false,
    screenReaderOptimized: preferences?.screen_reader_optimized || false,
    dyslexiaFriendlyFont: preferences?.dyslexia_friendly_font || false,
    theme: preferences?.theme || 'system',
  }

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext)
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider')
  }
  return context
}
